<template>
  <div class="settings-page visual-settings-page">
    <section class="resource-toolbar">
      <div>
        <h2>Visual</h2>
        <p>Defina a identidade exibida no painel e no app dos moradores.</p>
      </div>
      <v-btn depressed color="primary" :loading="saving" :disabled="loading" @click="save">
        <v-icon left size="18">mdi-content-save-outline</v-icon>
        Salvar
      </v-btn>
    </section>

    <section class="settings-panel">
      <div class="panel-heading">
        <h3>Identidade do local</h3>
        <p>Use nome, logomarca e cores para aproximar a plataforma da marca do condomínio, rua, bloco ou loteamento.</p>
      </div>

      <v-row dense>
        <v-col cols="12" md="5">
          <div class="logo-card">
            <div class="logo-preview" :style="{ backgroundColor: form.brand_secondary_color }">
              <img v-if="logoPreview" :src="logoPreview" alt="Logomarca do local" />
              <v-icon v-else :color="form.brand_primary_color" size="48">mdi-image-plus-outline</v-icon>
            </div>
            <v-file-input
              v-model="logoFile"
              :loading="loading"
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              label="Logomarca"
              prepend-icon=""
              prepend-inner-icon="mdi-file-image-outline"
              outlined
              dense
              hide-details="auto"
              @change="previewLogo"
            />
            <small>PNG, JPG, WEBP ou SVG. Recomendado: arquivo quadrado com fundo transparente.</small>
          </div>
        </v-col>

        <v-col cols="12" md="7">
          <v-text-field
            v-model="form.brand_name"
            label="Nome público do local"
            outlined
            dense
            hide-details="auto"
          />
          <v-text-field
            v-model="form.brand_short_name"
            label="Nome curto"
            outlined
            dense
            hide-details="auto"
            class="mt-3"
          />
          <v-row dense class="mt-1">
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.brand_primary_color"
                label="Cor principal"
                type="color"
                outlined
                dense
                hide-details="auto"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.brand_secondary_color"
                label="Cor de apoio"
                type="color"
                outlined
                dense
                hide-details="auto"
              />
            </v-col>
          </v-row>
          <v-text-field
            v-model="form.brand_support_text"
            label="Texto de apoio"
            outlined
            dense
            hide-details="auto"
            class="mt-3"
          />
        </v-col>
      </v-row>
    </section>

    <section class="settings-panel mt-4">
      <div class="panel-heading">
        <h3>Prévia</h3>
        <p>Visual de referência para conferir contraste, nome e aplicação da marca.</p>
      </div>

      <div
        class="brand-preview"
        :style="{
          '--preview-primary': form.brand_primary_color,
          '--preview-soft': form.brand_secondary_color,
        }"
      >
        <div class="preview-logo">
          <img v-if="logoPreview" :src="logoPreview" alt="" />
          <v-icon v-else color="var(--preview-primary)" size="34">mdi-home-city-outline</v-icon>
        </div>
        <div>
          <strong>{{ form.brand_name || "Nome do local" }}</strong>
          <span>{{ form.brand_support_text || "Gestão de locais" }}</span>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "VisualSettings",
  data() {
    return {
      loading: false,
      saving: false,
      logoFile: null,
      logoPreview: "",
      logoResourceId: null,
      form: {
        brand_name: "BOARUA",
        brand_short_name: "BOARUA",
        brand_support_text: "Gestão de locais",
        brand_primary_color: "#057a3d",
        brand_secondary_color: "#eaf5ee",
      },
    };
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;

      try {
        const response = await axios.get("/associations/current/profile");
        const content = response.data.content || {};
        const association = content.data || content || {};

        this.form = {
          brand_name: association.brand_name || association.name || this.form.brand_name,
          brand_short_name: association.brand_short_name || association.brand_name || association.name || this.form.brand_short_name,
          brand_support_text: association.brand_support_text || association.description || this.form.brand_support_text,
          brand_primary_color: association.brand_primary_color || this.form.brand_primary_color,
          brand_secondary_color: association.brand_secondary_color || this.form.brand_secondary_color,
        };

        this.logoResourceId = association.logo_resource_id || null;
        this.logoPreview = association.logo_url || "";
      } catch (error) {
        this.$dialog?.notify?.error?.("Nao foi possivel carregar a identidade visual.");
      } finally {
        this.loading = false;
      }
    },
    previewLogo(file) {
      if (!file) {
        this.logoPreview = "";
        return;
      }

      if (!["image/png", "image/jpeg", "image/webp", "image/svg+xml"].includes(file.type)) {
        this.logoFile = null;
        this.$dialog?.notify?.error("Envie uma imagem PNG, JPG, WEBP ou SVG.");
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        this.logoPreview = reader.result;
      };
      reader.readAsDataURL(file);
    },
    async uploadLogo() {
      if (!this.logoFile) {
        return this.logoResourceId;
      }

      const formData = new FormData();
      formData.append("file", this.logoFile);
      formData.append("name", "Logomarca do local");

      const response = await axios.post("/resources", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const content = response.data.content || {};
      return content.id || content.data?.id || null;
    },
    async save() {
      this.saving = true;

      try {
        const logoResourceId = await this.uploadLogo();

        await axios.patch("/associations/current/profile", {
          ...this.form,
          logo_resource_id: logoResourceId,
        });

        this.logoResourceId = logoResourceId;
        this.logoFile = null;
        this.$dialog?.notify?.success?.("Identidade visual salva.");
      } catch (error) {
        this.$dialog?.notify?.error?.("Nao foi possivel salvar a identidade visual.");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.resource-toolbar {
  margin-bottom: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;

  h2 {
    margin: 0;
    color: #111827;
    font-size: 20px;
  }

  p {
    margin: 3px 0 0;
    color: #66736b;
    font-size: 13px;
  }
}

.settings-panel {
  max-width: 860px;
  padding: 24px;
  background: #ffffff;
  border: 1px solid #e1e7e0;
  border-radius: 8px;
}

.panel-heading {
  margin-bottom: 16px;

  h3 {
    margin: 0;
    color: #111827;
    font-size: 18px;
  }

  p {
    margin: 4px 0 0;
    color: #66736b;
    font-size: 13px;
  }
}

.logo-card {
  height: 100%;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: #f7faf7;
  border: 1px solid #dfe9e2;
  border-radius: 8px;

  small {
    color: #66736b;
    line-height: 1.4;
  }
}

.logo-preview {
  min-height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed #bdd5c5;
  border-radius: 8px;
  overflow: hidden;

  img {
    max-width: 84%;
    max-height: 120px;
    object-fit: contain;
  }
}

.brand-preview {
  padding: 18px;
  display: flex;
  align-items: center;
  gap: 14px;
  background: linear-gradient(135deg, var(--preview-soft), #ffffff);
  border: 1px solid #dfe9e2;
  border-radius: 8px;

  strong,
  span {
    display: block;
  }

  strong {
    color: #111827;
    font-size: 20px;
    line-height: 1.1;
  }

  span {
    margin-top: 4px;
    color: #66736b;
  }
}

.preview-logo {
  width: 68px;
  height: 68px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  border: 1px solid #dfe9e2;
  border-radius: 8px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

@media (max-width: 720px) {
  .resource-toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .settings-panel {
    padding: 18px;
  }
}
</style>
